import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Tooltip from "@mui/material/Tooltip";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import DraggableDialog from "../../common/components/DraggableDialog";
import { Box, Typography, Divider, Chip } from "@mui/material";
import { Schedule as ScheduleIcon, EventAvailable as EventAvailableIcon } from "@mui/icons-material";
import { format } from "date-fns";

const OutagePopup = ({ row, outageData, selectedDate }) => {
  const [open, setOpen] = useState(false);
  const [matchedOutage, setMatchedOutage] = useState(null);

  const filterData = useCallback(
    (item) =>
      item.outage_Terminal_Key === row.rackPricing_Terminal_Key &&
      (
        // Match specific product OR "All Products" (when outage_FuelProduct_Key is null)
        item.outage_FuelProduct_Key === row.rackPricing_FuelProduct_Key ||
        item.outage_FuelProduct_Key === null ||
        item.outage_AllTerminalProducts === 1
      ) &&
      new Date(selectedDate) >= new Date(item.outage_EffDate) &&
      new Date(selectedDate) <= new Date(item.outage_ExpDate),
    [row, selectedDate]
  );

  const hasOutage = useMemo(
    () => Array.isArray(outageData) && outageData.some(filterData),
    [outageData, filterData]
  );

  const checkMatch = () => {
    if (!hasOutage) return;

    const match = outageData.find(filterData);

    if (match) {
      setMatchedOutage(match);
      setOpen(true);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return format(date, "MMM dd, yyyy 'at' h:mm a");
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Generate dialog title with product and terminal info
  const getDialogTitle = () => {
    const productDesc =
      row?.rackPricing_FuelProduct_RackDesc || "Unknown Product";
    const terminalDesc =
      row?.rackPricing_Terminal_RackDescription || "Unknown Terminal";
    return `Supply Status for ${productDesc} at ${terminalDesc}`;
  };

  return (
    <>
      <Tooltip title={hasOutage ? "View Outage Note" : "No Outage Note"}>
        <span>
          <ReportProblemIcon
            onClick={checkMatch}
            color={hasOutage ? "error" : "disabled"}
            fontSize="small"
            style={{ cursor: hasOutage ? "pointer" : "not-allowed" }}
          />
        </span>
      </Tooltip>

      {open && matchedOutage && (
        <DraggableDialog
          onClose={() => setOpen(false)}
          title={getDialogTitle()}
          maxWidth="md"
        >
          <Box sx={{ padding: "24px" }}>
            {/* Duration Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <ScheduleIcon color="primary" />
                Outage Duration
              </Typography>
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Chip
                    icon={<EventAvailableIcon />}
                    label="Effective Date"
                    variant="outlined"
                    color="warning"
                    sx={{ mb: 1, minWidth: "120px" }}
                  />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDateTime(matchedOutage.outage_EffDate)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Chip
                    icon={<EventAvailableIcon />}
                    label="Expiration Date"
                    variant="outlined"
                    color="success"
                    sx={{ mb: 1, minWidth: "120px" }}
                  />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatDateTime(matchedOutage.outage_ExpDate)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Note Section */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Outage Details
              </Typography>
              <Box
                sx={{
                  bgcolor: (theme) => theme.palette.mode === "light" ? "grey.50" : "grey.800",
                  p: 2,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: (theme) => theme.palette.mode === "light" ? "grey.200" : "grey.700"
                }}
              >
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                  {matchedOutage.outage_Note || "No additional details provided."}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DraggableDialog>
      )}
    </>
  );
};

OutagePopup.propTypes = {
  row: PropTypes.object.isRequired,
  outageData: PropTypes.array.isRequired,
  selectedDate: PropTypes.string.isRequired,
};

export default OutagePopup;
